import React, { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Upload,
  Image as ImageIcon,
  Video,
  File,
  X,
  Download,
  Trash2,
  Search,
  Grid3X3,
  List,
  Eye,
  Copy,
  Plus,
  FolderOpen,
  AlertCircle
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import ConfirmationModal from '../common/ConfirmationModal'

const MediaLibrary = () => {
  const [viewMode, setViewMode] = useState('grid')
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [fileToDelete, setFileToDelete] = useState(null)
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)

  const fileInputRef = useRef(null)
  const queryClient = useQueryClient()

  const { data: mediaResponse, isLoading, error } = useQuery(
    ['media', searchQuery, filterType],
    () => apiService.get('/media', { params: { search: searchQuery, type: filterType } }).then(res => res.data),
    { staleTime: 30 * 1000, retry: 1, refetchOnMount: true, refetchOnWindowFocus: true }
  )

  const mediaFiles = mediaResponse?.media || []

  const uploadMutation = useMutation(
    (formData) => apiService.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        setUploadProgress(prev => ({ ...prev, uploading: percentCompleted }))
      }
    }),
    {
      onSuccess: () => {
        toast.success('Files uploaded successfully!')
        queryClient.invalidateQueries(['media'])
        queryClient.refetchQueries(['media'])
        setShowUploadModal(false)
        setUploadProgress({})
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Upload failed')
        setUploadProgress({})
      }
    }
  )

  const deleteMutation = useMutation(
    (mediaId) => apiService.delete(`/media/${mediaId}`),
    {
      onSuccess: () => {
        toast.success('File deleted successfully!')
        queryClient.invalidateQueries(['media'])
        queryClient.refetchQueries(['media'])
        setSelectedFiles([])
      },
      onError: (error) => { toast.error(error.response?.data?.error || 'Delete failed') }
    }
  )

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files)
    if (files.length > 0) handleFileUpload(files)
    event.target.value = ''
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setDragOver(false)
    const files = Array.from(event.dataTransfer.files)
    handleFileUpload(files)
  }

  const handleFileUpload = (files) => {
    if (files.length === 0) return
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    setUploadProgress({ uploading: 0 })
    uploadMutation.mutate(formData)
  }

  const handleDragOver = (event) => { event.preventDefault(); setDragOver(true) }
  const handleDragLeave = (event) => { event.preventDefault(); setDragOver(false) }

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return ImageIcon
    if (mimeType?.startsWith('video/')) return Video
    return File
  }

  const getFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url)
    toast.success('URL copied to clipboard!')
  }

  const filteredFiles = mediaFiles.filter(file => {
    const matchesSearch = file.original_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         file.alt_text?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesType = filterType === 'all' ||
                       (filterType === 'images' && file.mime_type?.startsWith('image/')) ||
                       (filterType === 'videos' && file.mime_type?.startsWith('video/')) ||
                       (filterType === 'documents' && !file.mime_type?.startsWith('image/') && !file.mime_type?.startsWith('video/'))
    return matchesSearch && matchesType
  })

  if (error) {
    return (
      <div className="card card-pad" style={{ textAlign: 'center', padding: '3rem' }}>
        <FolderOpen style={{ width: '4rem', height: '4rem', color: 'var(--text-soft)', margin: '0 auto 1rem' }} />
        <h3 className="font-medium" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
          Unable to load media library
        </h3>
        <p className="text-muted">{error.response?.data?.error || 'There was an error loading your files.'}</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-bold" style={{ fontSize: '1.75rem' }}>Media Library</h1>
          <p className="text-muted">Manage your images, videos, and documents</p>
        </div>
        <button className="btn btn-primary flex items-center gap-2" onClick={() => setShowUploadModal(true)}>
          <Plus style={{ width: '1rem', height: '1rem' }} />
          Upload Files
        </button>
      </div>

      {/* Toolbar */}
      <div className="card" style={{ padding: '1rem' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center" style={{ gap: '1rem' }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <Search style={{
                position: 'absolute', left: '0.75rem',
                top: '50%', transform: 'translateY(-50%)',
                width: '1rem', height: '1rem',
                color: 'var(--text-soft)', pointerEvents: 'none'
              }} />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input"
                style={{ paddingLeft: '2.25rem', width: '16rem' }}
              />
            </div>

            {/* Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input"
            >
              <option value="all">All Files</option>
              <option value="images">Images</option>
              <option value="videos">Videos</option>
              <option value="documents">Documents</option>
            </select>
          </div>

          <div className="flex items-center" style={{ gap: '0.5rem' }}>
            {/* View Mode Toggle */}
            <div style={{
              display: 'flex',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              overflow: 'hidden'
            }}>
              <button
                className={viewMode === 'grid' ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                onClick={() => setViewMode('grid')}
                style={{ borderRadius: 0 }}
              >
                <Grid3X3 style={{ width: '1rem', height: '1rem' }} />
              </button>
              <button
                className={viewMode === 'list' ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                onClick={() => setViewMode('list')}
                style={{ borderRadius: 0 }}
              >
                <List style={{ width: '1rem', height: '1rem' }} />
              </button>
            </div>

            {/* Selection Actions */}
            {selectedFiles.length > 0 && (
              <div className="flex items-center" style={{ gap: '0.5rem' }}>
                <span className="text-muted" style={{ fontSize: '0.875rem' }}>{selectedFiles.length} selected</span>
                <button
                  className="btn btn-danger btn-sm flex items-center gap-1"
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  disabled={deleteMutation.isLoading}
                >
                  <Trash2 style={{ width: '1rem', height: '1rem' }} />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="card card-pad flex items-center justify-center" style={{ padding: '3rem' }}>
          <LoadingSpinner size="lg" text="Loading media files..." />
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="card card-pad" style={{ textAlign: 'center', padding: '3rem' }}>
          <FolderOpen style={{ width: '4rem', height: '4rem', color: 'var(--text-soft)', margin: '0 auto 1rem' }} />
          <h3 className="font-medium" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
            {searchQuery || filterType !== 'all' ? 'No files found' : 'No files uploaded yet'}
          </h3>
          <p className="text-muted" style={{ marginBottom: '1.5rem' }}>
            {searchQuery || filterType !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by uploading your first file.'
            }
          </p>
          <button className="btn btn-primary flex items-center gap-2" style={{ margin: '0 auto' }} onClick={() => setShowUploadModal(true)}>
            <Plus style={{ width: '1rem', height: '1rem' }} />
            Upload Files
          </button>
        </div>
      ) : (
        <div style={viewMode === 'grid'
          ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem' }
          : { display: 'flex', flexDirection: 'column', gap: '0.5rem' }
        }>
          {filteredFiles.map((file) => (
            <MediaFileCard
              key={file.id}
              file={file}
              viewMode={viewMode}
              isSelected={selectedFiles.includes(file.id)}
              onSelect={(selected) => {
                if (selected) {
                  setSelectedFiles([...selectedFiles, file.id])
                } else {
                  setSelectedFiles(selectedFiles.filter(id => id !== file.id))
                }
              }}
              onDelete={() => { setFileToDelete(file); setShowDeleteConfirm(true) }}
              onCopyUrl={() => copyToClipboard(file.public_url)}
              deleteMutation={deleteMutation}
            />
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onFileSelect={handleFileSelect}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          dragOver={dragOver}
          uploadProgress={uploadProgress}
          isUploading={uploadMutation.isLoading}
          fileInputRef={fileInputRef}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setFileToDelete(null) }}
        onConfirm={() => {
          if (fileToDelete) {
            deleteMutation.mutate(fileToDelete.id)
            setShowDeleteConfirm(false)
            setFileToDelete(null)
          }
        }}
        isLoading={deleteMutation.isLoading}
        title="Delete File"
        message={`Are you sure you want to delete "${fileToDelete?.original_name}"? This action cannot be undone.`}
        confirmText="Delete File"
        confirmVariant="destructive"
      />

      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={() => {
          selectedFiles.forEach(fileId => deleteMutation.mutate(fileId))
          setShowBulkDeleteConfirm(false)
        }}
        isLoading={deleteMutation.isLoading}
        title="Delete Files"
        message={`Are you sure you want to delete ${selectedFiles.length} ${selectedFiles.length === 1 ? 'file' : 'files'}? This action cannot be undone.`}
        confirmText={`Delete ${selectedFiles.length} ${selectedFiles.length === 1 ? 'File' : 'Files'}`}
        confirmVariant="destructive"
      />
    </div>
  )
}

const MediaFileCard = ({ file, viewMode, isSelected, onSelect, onDelete, onCopyUrl, deleteMutation }) => {
  const [showActions, setShowActions] = useState(false)
  const FileIcon = file.mime_type?.startsWith('image/') ? ImageIcon :
                   file.mime_type?.startsWith('video/') ? Video : File

  const getFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (viewMode === 'list') {
    return (
      <div
        className="card flex items-center justify-between"
        style={{ padding: '1rem', transition: 'box-shadow 0.2s' }}
      >
        <div className="flex items-center" style={{ gap: '1rem' }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          {file.mime_type?.startsWith('image/') ? (
            <img
              src={file.thumbnail_url || file.public_url}
              alt={file.alt_text || file.original_name}
              style={{ width: '3rem', height: '3rem', borderRadius: '0.25rem', objectFit: 'cover' }}
            />
          ) : (
            <div style={{
              width: '3rem', height: '3rem',
              borderRadius: '0.25rem',
              background: 'var(--surface-2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FileIcon style={{ width: '1.5rem', height: '1.5rem', color: 'var(--text-soft)' }} />
            </div>
          )}
          <div>
            <p className="font-medium" style={{ fontSize: '0.875rem' }}>{file.original_name}</p>
            <p className="text-muted" style={{ fontSize: '0.75rem' }}>
              {getFileSize(file.size)} · {new Date(file.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center" style={{ gap: '0.5rem' }}>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onCopyUrl} title="Copy URL">
            <Copy style={{ width: '1rem', height: '1rem' }} />
          </button>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => window.open(file.public_url, '_blank')} title="Preview">
            <Eye style={{ width: '1rem', height: '1rem' }} />
          </button>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            onClick={onDelete}
            disabled={deleteMutation.isLoading}
            title="Delete"
            style={{ color: 'var(--danger)' }}
          >
            <Trash2 style={{ width: '1rem', height: '1rem' }} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div
        style={{
          position: 'relative',
          cursor: 'pointer',
          borderRadius: 'var(--radius)',
          border: '2px solid var(--border)',
          overflow: 'hidden',
          background: 'var(--surface)',
          boxShadow: 'var(--shadow)',
          transition: 'box-shadow 0.3s'
        }}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        {/* Selection Checkbox */}
        <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', zIndex: 10 }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
        </div>

        {/* File Preview */}
        <div style={{
          aspectRatio: '1',
          padding: '1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--surface-2)',
          position: 'relative'
        }}>
          {file.mime_type?.startsWith('image/') ? (
            <img
              src={file.thumbnail_url || file.public_url}
              alt={file.alt_text || file.original_name}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '0.5rem' }}
            />
          ) : (
            <FileIcon style={{ width: '3rem', height: '3rem', color: 'var(--text-soft)' }} />
          )}

          {/* Actions Overlay */}
          {showActions && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.6)',
              borderRadius: 'var(--radius)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <div className="flex items-center" style={{ gap: '0.5rem' }}>
                {[
                  { icon: Eye, action: (e) => { e.stopPropagation(); window.open(file.public_url, '_blank') }, danger: false },
                  { icon: Copy, action: (e) => { e.stopPropagation(); onCopyUrl() }, danger: false },
                  { icon: Trash2, action: (e) => { e.stopPropagation(); onDelete() }, disabled: deleteMutation.isLoading, danger: true }
                ].map(({ icon: Icon, action, disabled, danger }, i) => (
                  <button
                    key={i}
                    onClick={action}
                    disabled={disabled}
                    style={{
                      padding: '0.375rem 0.5rem',
                      borderRadius: 'var(--radius)',
                      background: 'rgba(255,255,255,0.9)',
                      border: '1px solid rgba(255,255,255,0.5)',
                      cursor: 'pointer',
                      color: danger ? 'var(--danger)' : 'var(--text)'
                    }}
                  >
                    <Icon style={{ width: '1rem', height: '1rem' }} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* File Info */}
        <div style={{ padding: '1rem', background: 'var(--surface)' }}>
          <h3 className="font-semibold truncate" style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            {file.original_name}
          </h3>
          <p className="text-muted" style={{ fontSize: '0.75rem' }}>{getFileSize(file.size)}</p>
        </div>
      </div>
    </motion.div>
  )
}

const UploadModal = ({ isOpen, onClose, onFileSelect, onDrop, onDragOver, onDragLeave, dragOver, uploadProgress, isUploading, fileInputRef }) => {
  if (!isOpen) return null

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: '32rem' }}>
        <div className="flex items-center justify-between" style={{ padding: '1.5rem 1.5rem 1rem' }}>
          <h3 className="font-medium" style={{ fontSize: '1.125rem' }}>Upload Files</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <X style={{ width: '1.5rem', height: '1.5rem' }} />
          </button>
        </div>

        <div style={{ padding: '0 1.5rem 1rem' }}>
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            style={{
              border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 'var(--radius)',
              padding: '1.5rem',
              textAlign: 'center',
              background: dragOver ? 'var(--accent-soft)' : 'transparent',
              transition: 'all 0.2s'
            }}
          >
            <Upload style={{ width: '3rem', height: '3rem', color: 'var(--text-soft)', margin: '0 auto 1rem' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <p className="font-medium" style={{ fontSize: '1.125rem' }}>Drop files here or use button below</p>
              <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                Supports: Images, Videos, Documents (Max 10MB per file)
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx,.txt"
              onChange={onFileSelect}
              style={{ display: 'none' }}
            />
            <button
              className="btn btn-primary"
              onClick={() => fileInputRef.current?.click()}
              style={{ marginTop: '1rem' }}
              disabled={isUploading}
            >
              Select Files
            </button>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div style={{ marginTop: '1rem' }}>
              <div className="flex items-center justify-between" style={{ marginBottom: '0.5rem' }}>
                <span className="text-muted" style={{ fontSize: '0.875rem' }}>Uploading...</span>
                <span className="text-muted" style={{ fontSize: '0.875rem' }}>{uploadProgress.uploading || 0}%</span>
              </div>
              <div style={{ background: 'var(--surface-2)', borderRadius: '9999px', height: '0.5rem' }}>
                <div
                  style={{
                    background: 'var(--accent)',
                    height: '0.5rem',
                    borderRadius: '9999px',
                    width: `${uploadProgress.uploading || 0}%`,
                    transition: 'width 0.3s'
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end" style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface-2)'
        }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

export default MediaLibrary
